import moment from 'moment'
import { Column } from 'primereact/column'
import {
  DataTable,
  DataTableSortEvent,
  DataTableSortMeta,
} from 'primereact/datatable'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'

import Spinner from '../../App/Components/Spinner/Spinner'
import { useAppSelector } from '../../App/Hooks/reduxHooks'
import { formatDate } from '../../App/Util'
import {
  primeLimit,
  primeOffset,
  primeSortField,
  primeSortOrder,
  tableApiSort,
} from '../../App/Util/PrimeTableUtil'
import {
  PrimeTableCustomHeader,
  TParamFilterData,
} from '../../App/Util/PrimeTableUtil/PrimeTableCustomeHeader/PrimeTableCustomHeader'

import {
  useCustomerSubscriptionDataQuery,
  useCustomerSubscriptionInfoQuery,
} from './useCustomerSubscriptionQuery'

const resetCssMargin = { margin: 0 }
const columnWidth20 = { width: '20%' }
const columnWidth10 = { width: '10%' }

const FormatMomentDateColumn = (rowData: string | null) => {
  const { t } = useTranslation()

  return (
    <p style={resetCssMargin}>
      {rowData ? formatDate(moment(rowData), 'FORMAT_DATE_SHORT') : t('Unused')}
    </p>
  )
}

const StyledColumnBody = (columnValue: string) => (
  <p style={resetCssMargin}>{columnValue}</p>
)

export const CustomerSubscription = () => {
  const { t } = useTranslation()
  const { search } = useLocation()
  const history = useHistory()
  const urlSearchParams = useMemo(() => new URLSearchParams(search), [search])
  const establishmentId = useAppSelector(state =>
    state.establishment.active ? state.establishment.active.id : null,
  )

  // API Filter's
  const lastUsedAt = 'last_used_at'
  const lastUsedAtParam = urlSearchParams.get(lastUsedAt)
  const lastUsedAtParamArray = lastUsedAtParam
    ? lastUsedAtParam.replace('[', '').replace(']', '').split(',')
    : null
  const soldAt = 'sold_at'
  const soldAtParam = urlSearchParams.get(soldAt)
  const soldAtParamArray = soldAtParam
    ? soldAtParam.replace('[', '').replace(']', '').split(',')
    : null
  const subscriptionId = 'subscription_id'
  const subscriptionIdParam = urlSearchParams.get(subscriptionId)

  // API Search
  const searchParam = urlSearchParams.get('search') || undefined

  // API Sort
  const tableSortOrder = primeSortOrder(urlSearchParams.get('sort'))
  const tableSortField = primeSortField(urlSearchParams.get('sort'))
  const multiSortMeta: DataTableSortMeta[] | undefined = tableSortField
    ? [{ field: tableSortField, order: tableSortOrder }]
    : undefined

  // API Limit & Offset
  const limitParam = primeLimit(urlSearchParams.get('limit'))
  const offsetParam = primeOffset(urlSearchParams.get('offset'))

  const { data, status } = useCustomerSubscriptionDataQuery(
    Object.fromEntries(urlSearchParams.entries()),
  )

  const customerSubscriptionInfo = useCustomerSubscriptionInfoQuery(
    establishmentId ? establishmentId.toString() : '',
  )

  const onSort = (event: DataTableSortEvent) => {
    const newSearchParam = decodeURIComponent(
      tableApiSort(event, urlSearchParams),
    )
    history.push({
      pathname: window.location.pathname,
      search: newSearchParam,
    })
  }

  const onOffsetChange = (newOffset: number) => {
    urlSearchParams.set('offset', `${newOffset}`)
    const newSearchParam = urlSearchParams.toString()
    history.push({
      pathname: window.location.pathname,
      search: decodeURIComponent(newSearchParam),
    })
  }

  const borderHeaderAndBody = {
    padding: '12px',
    border: '1px solid #e5e7eb',
    borderWidth: '0 0 1px 0',
  }

  if (status === 'error') return <p>{t('Something went wrong')}</p>
  if (customerSubscriptionInfo.status === 'error') {
    return <p>{t('Could not get subscription info')}</p>
  }
  if (customerSubscriptionInfo.status === 'pending') return <Spinner />

  const apiFilterArray: TParamFilterData[] = [
    {
      list: null,
      value: lastUsedAtParamArray
        ? {
            from: lastUsedAtParamArray[0],
            to: lastUsedAtParamArray[1],
          }
        : null,
      name: lastUsedAt,
      type: 'date-range',
      label: 'Last used at',
    },
    {
      list: null,
      value: soldAtParamArray
        ? {
            from: soldAtParamArray[0],
            to: soldAtParamArray[1],
          }
        : null,
      name: soldAt,
      type: 'date-range',
      label: 'Last sold at',
    },
    {
      value: subscriptionIdParam ? subscriptionIdParam : null,
      list: customerSubscriptionInfo
        ? customerSubscriptionInfo.data.map(subscription => ({
            label: subscription.name,
            value: subscription.id.toString(),
          }))
        : null,
      name: subscriptionId,
      type: 'select',
      label: 'Subscription',
    },
  ]

  return (
    <>
      <PrimeTableCustomHeader
        hasApiSearch
        apiSearchInput={searchParam}
        apiFilterArray={apiFilterArray}
      />
      <DataTable
        value={data?.list}
        removableSort
        stripedRows
        paginator
        rows={limitParam || 20}
        first={offsetParam || 0}
        onPage={e => onOffsetChange(e.first)}
        totalRecords={data?.totalAmount || 0}
        lazy
        loading={status === 'pending'}
        onSort={onSort}
        emptyMessage={t('No customers found.')}
        sortMode="multiple"
        multiSortMeta={multiSortMeta}>
        <Column
          headerStyle={borderHeaderAndBody}
          bodyStyle={borderHeaderAndBody}
          sortable
          field="customer.first_name"
          header={t('Customer')}
          style={columnWidth20}
          body={columnData => (
            <p style={{ margin: 0 }}>
              {columnData.customer.first_name} {columnData.customer.last_name}
            </p>
          )}
        />
        <Column
          headerStyle={borderHeaderAndBody}
          bodyStyle={borderHeaderAndBody}
          sortable
          field="name"
          header={t('Subscription')}
          style={columnWidth20}
          body={columnData => StyledColumnBody(columnData.name)}
        />
        <Column
          headerStyle={borderHeaderAndBody}
          bodyStyle={borderHeaderAndBody}
          sortable
          field="sold_at"
          header={t('Sold at')}
          style={columnWidth20}
          body={columnData => FormatMomentDateColumn(columnData.sold_at)}
        />
        <Column
          headerStyle={borderHeaderAndBody}
          bodyStyle={borderHeaderAndBody}
          sortable
          field="last_used_at"
          header={t('Last used at')}
          style={columnWidth20}
          body={columnData => FormatMomentDateColumn(columnData.last_used_at)}
        />
        <Column
          headerStyle={borderHeaderAndBody}
          bodyStyle={borderHeaderAndBody}
          sortable
          field="turns_used"
          header={t('Turns used')}
          style={columnWidth10}
          body={columnData => StyledColumnBody(columnData.turns_used)}
        />
        <Column
          headerStyle={borderHeaderAndBody}
          bodyStyle={borderHeaderAndBody}
          sortable
          field="turns"
          header={t('Turns')}
          style={columnWidth10}
          body={columnData => StyledColumnBody(columnData.turns)}
        />
      </DataTable>
    </>
  )
}
